import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GuestPreviewSignupWall from './GuestPreviewSignupWall';
import TypingMessage from './TypingMessage';
import {
  clearGuestPreviewState,
  getDefaultGuestPreviewState,
  loadGuestPreviewState,
  saveGuestPreviewState,
  sendGuestPreviewMessage,
} from '../services/guestPreviewService';
import {
  trackGuestPreviewLoginClick,
  trackGuestPreviewMessageSent,
  trackGuestPreviewShown,
  trackGuestPreviewSignupClick,
  trackGuestPreviewStarted,
  trackGuestPreviewWallShown,
} from '../services/visitService';

const GuestPreviewChatCard = ({ onLoginRequest }) => {
  const navigate = useNavigate();
  const [guestState, setGuestState] = useState(() => loadGuestPreviewState());
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [hasTrackedStart, setHasTrackedStart] = useState(false);
  const [typingMsgIndex, setTypingMsgIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Only refocus on non-touch devices — on mobile let the keyboard close so
  // the user can scroll up to read the reply.
  const refocusInput = () => {
    const isMobile = navigator.maxTouchPoints > 1 || /Mobi|Android/i.test(navigator.userAgent);
    if (isMobile) return;
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (inputRef.current && !inputRef.current.disabled) {
          inputRef.current.focus();
        }
      }, 50);
    });
  };

  useEffect(() => {
    trackGuestPreviewShown();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [guestState.messages, status]);

  useEffect(() => {
    saveGuestPreviewState(guestState);
  }, [guestState]);

  const isWallVisible = Boolean(guestState.wallReason);
  const isInputDisabled = status === 'sending' || isWallVisible;

  const handleReset = () => {
    const freshState = getDefaultGuestPreviewState();
    clearGuestPreviewState();
    setGuestState(freshState);
    setInput('');
    setStatus('idle');
    setError('');
    setHasTrackedStart(false);
  };

  const handleSignup = async (reason) => {
    await trackGuestPreviewSignupClick(reason || guestState.wallReason || 'unknown');
    navigate('/signup', {
      state: {
        source: 'guest-preview',
        guestPreviewReason: reason || guestState.wallReason || 'unknown',
      },
    });
  };

  const handleLogin = async (reason) => {
    await trackGuestPreviewLoginClick(reason || guestState.wallReason || 'unknown');
    if (typeof onLoginRequest === 'function') {
      onLoginRequest('guest-preview');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedInput = input.trim();

    if (!trimmedInput || isInputDisabled) {
      return;
    }

    const nextMessages = [
      ...guestState.messages,
      {
        role: 'user',
        content: trimmedInput,
      },
    ];
    const nextGuestMessageCount = (guestState.guestMessageCount || 0) + 1;

    if (!hasTrackedStart) {
      trackGuestPreviewStarted();
      setHasTrackedStart(true);
    }

    trackGuestPreviewMessageSent(nextGuestMessageCount);

    setGuestState((current) => ({
      ...current,
      messages: nextMessages,
    }));
    setInput('');
    setError('');
    setStatus('sending');

    try {
      const result = await sendGuestPreviewMessage({
        sessionId: guestState.sessionId,
        messages: nextMessages,
        latestMessage: trimmedInput,
        guestMessageCount: nextGuestMessageCount,
      });

      if (result.status === 'reply') {
        setGuestState((current) => {
          const updatedMessages = [...nextMessages, result.message];
          setTypingMsgIndex(updatedMessages.length - 1);
          return {
            ...current,
            guestMessageCount: result.guestMessageCount || current.guestMessageCount,
            messages: updatedMessages,
            wallReason: null,
            wallMessage: '',
          };
        });
        setStatus('reply');
        return;
      }

      if (result.status === 'signup_required') {
        setGuestState((current) => ({
          ...current,
          guestMessageCount: result.guestMessageCount || current.guestMessageCount,
          wallReason: result.reason,
          wallMessage: result.message,
        }));
        setStatus('signup_required');
        trackGuestPreviewWallShown(result.reason);
        return;
      }

      if (result.status === 'rate_limited') {
        setStatus('rate_limited');
        setError(result.message || 'Please wait a moment before sending another message.');
        return;
      }

      setStatus('idle');
    } catch (requestError) {
      console.error('Guest preview request failed:', requestError);
      setStatus('error');
      setError(requestError.message || 'Unable to send message right now.');
    } finally {
      refocusInput();
    }
  };

  return (
    <div className="rounded-[28px] border border-white/60 bg-white/90 p-5 shadow-[0_24px_80px_rgba(15,23,42,0.12)] backdrop-blur-sm md:p-6">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
        <div className="flex items-center gap-3">
          <img
            src="/images/bela-avatar-s.png"
            alt="Bela"
            className="h-12 w-12 rounded-full object-cover shadow-sm"
          />
          <div>
            <p className="text-sm font-semibold text-slate-900">Bela</p>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="h-2 w-2 rounded-full bg-emerald-400"></span>
              <span>Online now</span>
            </div>
          </div>
        </div>
        <div className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-500">
          Chat with Bela
        </div>
      </div>


      <div className="chat-scroll mt-4 h-[55vh] max-h-[500px] min-h-[380px] space-y-3 overflow-y-auto rounded-2xl bg-slate-50/80 p-3 md:h-[480px]">
        {guestState.messages.map((message, index) => {
          const isUser = message.role === 'user';
          return (
            <div
              key={`${message.role}-${index}-${message.content.slice(0, 12)}`}
              className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 text-left text-sm leading-6 shadow-sm ${
                  isUser
                    ? 'rounded-br-md bg-slate-900 text-white'
                    : 'rounded-bl-md border border-white bg-white text-slate-700'
                }`}
              >
                {!isUser && index === typingMsgIndex ? (
                  <TypingMessage
                    content={message.content}
                    onDone={() => setTypingMsgIndex(null)}
                  />
                ) : (
                  message.content
                )}
              </div>
            </div>
          );
        })}

        {status === 'sending' ? (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-md border border-white bg-white px-4 py-3 shadow-sm">
              <div className="typing-dots" aria-label="Bela is typing">
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
              </div>
            </div>
          </div>
        ) : null}
        <div ref={messagesEndRef} />
      </div>

      {isWallVisible ? (
        <div className="mt-4">
          <GuestPreviewSignupWall
            reason={guestState.wallReason}
            message={guestState.wallMessage}
            onSignup={() => handleSignup(guestState.wallReason)}
            onLogin={() => handleLogin(guestState.wallReason)}
          />
        </div>
      ) : null}

      {error ? (
        <div className="mt-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="mt-4">
        <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 focus-within:border-rose-300 focus-within:ring-2 focus-within:ring-rose-100">
          <input
            type="text"
            value={input}
            onChange={(event) => setInput(event.target.value.slice(0, 280))}
            placeholder={isWallVisible ? 'Create an account to continue…' : 'What would you like to know?'}
            className="h-10 flex-1 border-0 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
            disabled={isInputDisabled}
            ref={inputRef}
          />
          <button
            type="submit"
            disabled={!input.trim() || isInputDisabled}
            className="inline-flex h-10 items-center justify-center rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 px-4 text-sm font-semibold text-white transition hover:from-rose-600 hover:to-pink-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </form>

    </div>
  );
};

export default GuestPreviewChatCard;
